import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveBuyer } from '../../../store/buyerAPI/buyer/reducers/buyer.slice';
import { AppDispatch, RootState } from '../../../store/store';
import { BuyerResponseDto } from '../../../store/buyerAPI/buyer/dto/response/buyer-response.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import cn from 'classnames';
import styles from './OrganizationAddPage.module.css';
import { BuyerSaveRequestDto } from '../../../store/buyerAPI/buyer/dto/request/buyer-save-request.dto';
import { LandscapeBuyerRelationType } from '../../../store/landscapeAPI/landscape/landscape-buyer-relation-type';
import { getUUID } from '../../../utils/UUID';
import { OrganizationAddTemplate } from '../../templates';
import Spinner from '../../atoms/Spinner/Spinner';
import { PromiseSettledResult } from '../../../utils/types/types';

interface OrganizationAddPageProps {}

const OrganizationAddPage: FC<OrganizationAddPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [imageMeta, setImageMeta] = useState<ImageMeta>();
  const [logoMeta, setLogoMeta] = useState<ImageMeta>();
  const [organization, setOrganization] = useState<BuyerResponseDto>({
    name: '',
    logo: '',
    description: '',
    profileType: LandscapeBuyerRelationType.Funders,
    backgroundImage: '',
    url: '',
    documents: [],
    id: getUUID(),
    versionId: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    language: '',
    published: true,
  });
  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations: () => Promise<
    PromiseSettledResult<void>[]
  > = async () => {
    const rejectedResults: PromiseSettledResult<void>[] = [
      {
        status: 'rejected',
        reason: new Error(
          'Must save verification status before generating translation'
        ),
      },
    ];

    return Promise.reject(rejectedResults);
  };

  const { status } = useSelector((state: RootState) => state.buyerSlice);
  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const generateTranslation = async () => {};

  const handleBackgroundOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const handleLogoOnChange = (imageMeta: ImageMeta) => {
    setLogoMeta(imageMeta);
  };

  const handleOnChange = (data: BuyerResponseDto) => {
    setOrganization(data);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const onSubmit = async (data: BuyerResponseDto) => {
    const logo = await processMedia(data.logo ?? '', logoMeta);

    const image = await processMedia(data.backgroundImage ?? '', imageMeta);

    const organizationToSave: BuyerSaveRequestDto = {
      id: data.id,
      published: data.published,
      name: data.name ?? '',
      logo: logo.href,
      description: data.description ?? '',
      profileType: data.profileType,
      backgroundImage: image.href,
      url: data.url ?? '',
    };

    const response = await dispatch(
      saveBuyer({ data: organizationToSave, language: selectedLanguage })
    );
    const newBuyer = response.payload as BuyerResponseDto;

    navigate(`/organizations/edit/${newBuyer.id}`);
  };

  if (status === 'loading' || mediaStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      <OrganizationAddTemplate
        handleOnChange={handleOnChange}
        data={organization}
        onSubmit={onSubmit}
        handleBackgroundOnChange={handleBackgroundOnChange}
        handleLogoOnChange={handleLogoOnChange}
        setLanguage={setLanguage}
        selectedLanguage={selectedLanguage}
        fromLanguage={fromLanguage}
        setFromLanguage={setFromLanguage}
        toLanguage={toLanguage}
        setToLanguage={setToLanguage}
        generateTranslation={generateTranslation}
        generateAllTranslations={generateAllTranslations}
      />
    </div>
  );
};

export default OrganizationAddPage;
